import { useTranslation } from 'next-i18next';
import isDateExact from '@bridebook/web/lib/weddings/utils/is-date-exact';
import { format } from 'date-fns';
import { IWedding } from '@bridebook/models/source/models/Weddings.types';
import { useMemo } from 'react';

interface UseWeddingWebsiteMetadataProps {
  partners?: string[];
  date?: IWedding['date'];
  baseUrl?: string;
}

export const useWeddingWebsiteMetadata = ({ partners, date }: UseWeddingWebsiteMetadataProps) => {
  const { t } = useTranslation('weddingWebsite');

  const hasDate = date && isDateExact(date);
  const dateString = hasDate ? format(new Date(date.y, date.m, date.d), 'dd.MM.yy') : '';

  const imageText = useMemo(() => {
    if (partners && partners[0] && partners[1]) {
      return `${partners[0]} & ${partners[1]}`;
    }
    return t('metadata.website.defaultTitle');
  }, [partners, t]);

  // Metadata for Wedding Website
  const websiteTitle = useMemo(
    () =>
      partners && partners[0] && partners[1]
        ? t('metadata.website.titleWithNames', {
            firstPartnerName: partners[0],
            secondPartnerName: partners[1],
          })
        : t('metadata.website.defaultTitle'),
    [partners, t],
  );

  const websiteDescription = useMemo(() => {
    if (hasDate) {
      return dateString;
    }
    return t('metadata.website.defaultDescription');
  }, [dateString, hasDate, t]);

  // Metadata for RSVP Page
  const rsvpTitle = useMemo(() => {
    if (partners && partners[0] && partners[1]) {
      return t('metadata.rsvp.titleWithNames', {
        firstPartnerName: partners[0],
        secondPartnerName: partners[1],
      });
    }
    return t('metadata.rsvp.defaultTitle');
  }, [partners, t]);

  const rsvpDescription = useMemo(() => {
    if (date && isDateExact(date)) {
      return t('metadata.rsvpDescriptionWithDate', {
        date: dateString,
      });
    }
    return t('metadata.rsvpDefaultDescription');
  }, [date, dateString, t]);

  return {
    imageText,
    websiteTitle,
    websiteDescription,
    rsvpTitle,
    rsvpDescription,
  };
};
